import { useRef, useEffect, useCallback } from 'react'
import { dia } from '@joint/core'
import { debounce } from 'lodash'
import { UseMutationResult } from '@tanstack/react-query'

import { useFloorStore } from '../../../../map-editor-shared/store/floorStore'
import { useCsvRuntimeStore } from '../../../../map-editor-features/csv/model/csvStore'
import type { ParkingLot } from '../../../../map-editor-shared/store/projectStore'
import type {
  UpdateProjectDataResponse,
  MapData as ApiMapData
} from '../../../../map-editor-shared/api/types/mapData'

interface UseFloorAutoSaveProps {
  graph: dia.Graph | null
  paper: dia.Paper | null
  selectedElementId: string | null
  projectId: number
  currentLot: string
  currentLotData: ParkingLot | undefined
  renderScale: number
  updateMapDataMutation: UseMutationResult<
    UpdateProjectDataResponse,
    Error,
    { projectId: number; mapData: ApiMapData },
    unknown
  >
}

interface UseFloorAutoSaveReturn {
  autoSaveToServer: () => void
}

export function useFloorAutoSave(props: UseFloorAutoSaveProps): UseFloorAutoSaveReturn {
  const {
    graph,
    paper,
    selectedElementId,
    projectId,
    currentLot,
    currentLotData,
    renderScale,
    updateMapDataMutation,
  } = props

  const {
    currentFloor,
    floors,
    updateFloorMapData,
  } = useFloorStore()

  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const debouncedServerSaveRef = useRef<ReturnType<typeof debounce> | null>(null)

  // ======================
  // Helper: Auto-save to server (2-second debounced)
  // ======================
  const autoSaveToServer = useCallback(() => {
    if (!currentFloor || !projectId || !currentLot || !graph) {
      return
    }

    // Cancel any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
      autoSaveTimeoutRef.current = null
    }

    // Clear the selected element
    const savedSelectedId = selectedElementId
    if (savedSelectedId && paper) {
      const cell = graph.getCell(savedSelectedId)
      if (cell) {
        cell.attr('body/stroke', '#000000')
        cell.attr('body/strokeWidth', 1)
      }
    }

    try {
      const graphJson = graph.toJSON()

      const activeFloorId = useFloorStore.getState().currentFloor
      const csvSnap = activeFloorId
        ? useCsvRuntimeStore.getState().getCsvRuntimeForFloor(activeFloorId)
        : null
      const csvData = csvSnap
        ? {
            csvRawData: csvSnap.rawData,
            csvParsedData: csvSnap.parsedData,
            csvGroupedLayers: csvSnap.groupedLayers,
            csvSelectedLayers: Array.from(csvSnap.selectedLayers),
            csvFileName: csvSnap.uploadState.status === 'parsed' ? csvSnap.uploadState.fileName : null,
          }
        : {
            csvRawData: null,
            csvParsedData: null,
            csvGroupedLayers: null,
            csvSelectedLayers: [],
            csvFileName: null,
          }

      updateFloorMapData(currentFloor, {
        graphJson,
        ...csvData,
        renderScale,
      })

      // Use the debounced server save function
      if (debouncedServerSaveRef.current) {
        debouncedServerSaveRef.current()
      }
    } catch (error) {
    }

    // Restore the selected element
    if (savedSelectedId && paper) {
      const cell = graph.getCell(savedSelectedId)
      if (cell) {
        cell.attr('body/stroke', '#2196f3')
        cell.attr('body/strokeWidth', 2)
      }
    }
  }, [
    currentFloor,
    projectId,
    currentLot,
    graph,
    paper,
    selectedElementId,
    renderScale,
    updateFloorMapData,
  ])

  // ======================
  // Effect: Initialize debounced server save function
  // ======================
  useEffect(() => {
    debouncedServerSaveRef.current = debounce(() => {
      // Read freshest state at debounce fire time (not stale closure)
      const { floors: latestFloors, currentFloor: latestCurrentFloor } = useFloorStore.getState()

      if (!latestCurrentFloor || !projectId || !currentLot || !graph) {
        return
      }

      const currentFloorData = latestFloors.find(f => f.id === latestCurrentFloor)
      if (!currentFloorData?.mapData) {
        return
      }

      const projectFloors = latestFloors.filter(f => f.lotId === currentLot)
      // 빈 graphJson 도 floor 메타데이터(name/order)는 보존되어야 라운드트립 시 사용자
      // 입력 floor 이름이 살아있다. 빈 그래프는 { cells: [] } 로 채워서 서버 NOT NULL
      // 제약과 충돌하지 않게 한다.
      const floorsPayload = projectFloors.map(floor => ({
        name: floor.name,
        order: floor.order,
        graphJson: floor.mapData?.graphJson ?? { cells: [] },
      }))

      if (floorsPayload.length === 0) {
        return
      }

      const mapDataPayload: ApiMapData = {
        floors: floorsPayload,
        template_id: currentLotData?.templateId
      }

      updateMapDataMutation.mutate({
        projectId,
        mapData: mapDataPayload,
      })
    }, 2000)

    return () => {
      debouncedServerSaveRef.current?.cancel()
    }
  // floors removed from deps — read via getState() inside debounce to avoid stale closure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentLot, graph, updateMapDataMutation])

  return {
    autoSaveToServer,
  }
}
